<script setup>
/* global axios */
import { ref, reactive, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BotRights from './BotRights.vue';
import Draggable from 'vuedraggable';
import GroupOrderHandle from './GroupOrderHandle.vue';
import GroupMode from './GroupMode.vue';
import SmmPurchaseButton from '../../finance/SmmPurchaseButton.vue';

const route = useRoute();
const router = useRouter();
const rows = ref([]),
  bots = ref([]),
  total = ref(0),
  page = ref(1);
const expandedBots = ref(new Set());
const receiverReady = ref(false),
  modeSaving = ref(null),
  modeUncertain = ref(new Set());
const receiverPending = ref(null),
  receiverPendingMedia = ref(null),
  receiverOldestAt = ref(null);
const managementReady = ref(false);
const modeDialog = ref(null),
  modeInfo = ref(null),
  modeLoading = ref(false),
  modeError = ref('');
let infoEpoch = 0;
async function setMode(group, mode) {
  if (modeSaving.value !== null || modeUncertain.value.has(group.id)) return;
  const generation = epoch;
  modeSaving.value = group.id;
  notice.value = '';
  try {
    const result = await request(`/${group.id}/mode`, 'patch', {
      mode,
      mode_revision: group.mode_revision,
    });
    if (generation !== epoch) return;
    Object.assign(group, result);
    notice.value =
      mode === 'off'
        ? 'Режим выключен. Новые сообщения группы не обрабатываются.'
        : mode === 'manage'
          ? 'Управление включено. В диалоге можно отправлять и удалять сообщения вручную. Автомодерация выключена.'
          : 'Наблюдение включено. Сообщения появляются в диалоге. Модерация не включена.';
  } catch (e) {
    if (generation !== epoch) return;
    if (denied.value) {
      error.value = e.message;
      return;
    }
    modeUncertain.value = new Set([...modeUncertain.value, group.id]);
    modeError.value = e.message;
    await showMode(group, false);
  } finally {
    if (generation === epoch) modeSaving.value = null;
  }
}
async function showMode(group, fetch = true) {
  const generation = epoch,
    current = ++infoEpoch;
  modeInfo.value = { ...group };
  if (fetch) modeError.value = '';
  await nextTick();
  modeDialog.value?.open();
  if (!fetch) return;
  modeLoading.value = true;
  try {
    const result = await request(`/${group.id}/mode`);
    if (generation === epoch && current === infoEpoch) modeInfo.value = result;
  } catch (e) {
    if (generation === epoch && current === infoEpoch)
      modeError.value = e.message;
  } finally {
    if (generation === epoch && current === infoEpoch)
      modeLoading.value = false;
  }
}
const receiverStatus = computed(() => {
  const data = modeInfo.value;
  if (!data || data.mode === 'off')
    return 'Получение для этой группы выключено';
  if (!data.receiver_ready)
    return 'Получатель недоступен. Сообщения сейчас не получаются.';
  if (data.receiver_error)
    return (
      messages[data.receiver_error] ||
      'Ошибка получения. Показаны последние сохранённые данные.'
    );
  if (
    !data.receiver_checked_at ||
    Date.now() - Date.parse(data.receiver_checked_at) > 180000
  )
    return 'Ожидаем проверку получателя';
  return 'Получатель работает. Ожидаем новые сообщения группы.';
});
const orderVersion = ref(''),
  orderSaving = ref(false),
  orderUncertain = ref(false),
  orderMessage = ref('');
let orderBefore = [];
const dragOptions = {
  handle: '.tg-group-drag-handle',
  animation: 150,
  delay: 220,
  delayOnTouchOnly: true,
  touchStartThreshold: 5,
  ghostClass: 'tg-group-sort-ghost',
  chosenClass: 'tg-group-sort-chosen',
};
function startOrder() {
  orderBefore = [...rows.value];
  orderMessage.value = '';
}
async function saveOrder({ moved } = {}) {
  if (!moved || orderSaving.value) return;
  const generation = epoch;
  const index = rows.value.findIndex(row => row.id === moved.element.id);
  const next = rows.value[index + 1],
    previous = rows.value[index - 1];
  if (!next && !previous) return;
  orderSaving.value = true;
  try {
    const data = await request('/order', 'patch', {
      id: moved.element.id,
      anchor_id: (next || previous).id,
      direction: next ? 'before' : 'after',
      order_version: orderVersion.value,
    });
    if (generation !== epoch) return;
    orderVersion.value = data.order_version;
    orderMessage.value = 'Порядок групп сохранён.';
  } catch (e) {
    if (generation !== epoch) return;
    rows.value = orderBefore;
    orderUncertain.value = true;
    orderMessage.value =
      e.code === 'order_conflict'
        ? 'Список изменён с другого устройства. Обновите список перед перемещением.'
        : 'Не удалось подтвердить сохранение порядка. Обновите список перед повтором.';
  } finally {
    if (generation === epoch) orderSaving.value = false;
  }
}
function moveByKey(index, step) {
  if (
    orderSaving.value ||
    orderUncertain.value ||
    loading.value ||
    index + step < 0 ||
    index + step >= rows.value.length
  )
    return;
  startOrder();
  const reordered = [...rows.value],
    [element] = reordered.splice(index, 1);
  reordered.splice(index + step, 0, element);
  rows.value = reordered;
  saveOrder({ moved: { element } });
}
function toggleBot(id) {
  const next = new Set(expandedBots.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expandedBots.value = next;
}
function openPublications(group) {
  router.push({
    name: 'telegram_publications',
    params: { accountId: route.params.accountId },
    query: { group: String(group.id), view: 'month' },
  });
}
const loading = ref(false),
  busy = ref(false),
  denied = ref(false);
const error = ref(''),
  formError = ref(''),
  notice = ref('');
const formErrorCode = ref('');
const dialog = ref(null),
  preview = ref(null);
const form = reactive({ chat_id: '', bot_entry_id: '' });
const requests = new Set();
let epoch = 0,
  formEpoch = 0;
const messages = {
  invalid_mode: 'Недопустимый режим.',
  mode_conflict:
    'Режим изменён с другого устройства. Обновите список перед повтором.',
  receiver_unavailable:
    'Получатель пока недоступен. Режим не включён. Обновите список позже.',
  webhook_present:
    'Этот бот подключён к другому получателю (webhook). Мы ничего не отключали. Нужна проверка подключения.',
  receiver_conflict:
    'Telegram сообщил о другом получателе этого бота. Получение остановлено: сначала устраните конфликт, затем выключите и снова включите наблюдение.',
  configuration_changed:
    'Настройки бота изменились. Получение будет повторено с новыми настройками.',
  receiver_failed:
    'Ошибка получения. Автоматический повтор — не ранее чем через 5 минут.',
  delivery_backlog:
    'Сообщения ожидают обработки. Если очередь не уменьшается, проверьте подключение.',
  forbidden: 'Раздел доступен только администратору.',
  invalid_input:
    'Введите юзернейм без @, например beauty_nn_52, или числовой Chat ID закрытой группы.',
  not_found: 'Бот не найден. Обновите список подключений.',
  duplicate: 'Эта группа уже добавлена.',
  not_a_group: 'Указанный чат не является Telegram-группой.',
  bot_not_in_group: 'Добавьте выбранного бота в эту группу в Telegram.',
  bot_not_admin: 'Сделайте бота администратором группы в Telegram.',
  bot_rights_missing:
    'Боту нужны права удалять сообщения и ограничивать участников. Выдайте их в Telegram.',
  chat_unavailable:
    'Группа недоступна боту. Проверьте юзернейм или Chat ID и добавьте бота в группу.',
  verification_expired: 'Результат проверки устарел. Проверьте группу ещё раз.',
  rate_limited: 'Повторная проверка доступна через 30 секунд.',
  busy: 'Сейчас выполняется другая операция с ботом или прокси. Эта проверка не началась. Подождите несколько секунд и повторите — введённые данные сохранены.',
  limit_reached: 'Достигнут лимит: 500 групп.',
  token_invalid:
    'Telegram отклонил токен бота. Проверьте настройки в разделе «ТГ Бот».',
  telegram_rate_limit: 'Telegram ограничил запросы. Повторите позже.',
  telegram_unavailable: 'Telegram временно недоступен.',
  proxy_unavailable:
    'Нет связи через прокси. Прямое подключение не использовалось.',
  proxy_auth_failed: 'Прокси отклонила авторизацию.',
  credentials_unavailable: 'Не удалось прочитать реквизиты подключения бота.',
  timeout: 'Telegram не ответил вовремя. Проверьте прокси и повторите позже.',
  tls_failed: 'Ошибка защищённого соединения с Telegram.',
  invalid_response: 'Telegram вернул неожиданные данные. Группа не добавлена.',
};
const canCheck = computed(
  () =>
    /^(?:-[1-9]\d{0,15}|@?[A-Za-z][A-Za-z0-9_]{4,31})$/.test(
      form.chat_id.trim()
    ) &&
    !!form.bot_entry_id &&
    !busy.value
);
const date = value =>
  value ? new Date(value).toLocaleString('ru-RU') : 'Ещё не обновлялось';
const count = value =>
  Number.isInteger(value) ? value.toLocaleString('ru-RU') : '—';

async function request(path = '', method = 'get', data) {
  const generation = epoch;
  const controller = new AbortController();
  requests.add(controller);
  try {
    return (
      await axios({
        url: `/api/v1/accounts/${route.params.accountId}/tg_groups${path}`,
        method,
        data,
        signal: controller.signal,
        timeout: 40000,
      })
    ).data;
  } catch (e) {
    if (generation === epoch && [401, 403].includes(e.response?.status)) {
      denied.value = true;
      rows.value = [];
      bots.value = [];
      dialog.value?.close();
      modeDialog.value?.close();
      modeInfo.value = null;
    }
    const failure = new Error(
      messages[e.response?.data?.error] ||
        'Запрос не выполнен. Обновите список перед повтором.'
    );
    failure.code = e.response?.data?.error;
    throw failure;
  } finally {
    requests.delete(controller);
  }
}
async function load(targetPage = page.value) {
  const generation = epoch;
  loading.value = true;
  error.value = '';
  denied.value = false;
  try {
    const data = await request(`?page=${targetPage}`);
    if (generation !== epoch) return;
    rows.value = data.items;
    bots.value = data.bots;
    total.value = data.total;
    page.value = data.page;
    orderVersion.value = data.order_version;
    orderUncertain.value = false;
    orderMessage.value = '';
    receiverReady.value = data.receiver_ready === true;
    modeUncertain.value = new Set();
    receiverPending.value = data.receiver_pending;
    receiverPendingMedia.value = data.receiver_pending_media;
    receiverOldestAt.value = data.receiver_oldest_pending_at;
    managementReady.value = data.management_ready === true;
  } catch (e) {
    if (generation === epoch) error.value = e.message;
  } finally {
    if (generation === epoch) loading.value = false;
  }
}
function reset() {
  formEpoch += 1;
  preview.value = null;
  formError.value = '';
  formErrorCode.value = '';
  form.chat_id = '';
  form.bot_entry_id = bots.value.length === 1 ? bots.value[0].id : '';
}
function back() {
  if (busy.value) return;
  formEpoch += 1;
  preview.value = null;
  formError.value = '';
  formErrorCode.value = '';
}
async function open() {
  reset();
  await nextTick();
  dialog.value.open();
}
async function check() {
  if (!canCheck.value) return;
  const generation = epoch,
    currentForm = formEpoch;
  busy.value = true;
  formError.value = '';
  formErrorCode.value = '';
  preview.value = null;
  try {
    const data = await request('/resolve', 'post', {
      chat_id: form.chat_id.trim(),
      bot_entry_id: form.bot_entry_id,
    });
    if (generation === epoch && currentForm === formEpoch) preview.value = data;
  } catch (e) {
    if (generation === epoch && currentForm === formEpoch) {
      formError.value = e.message;
      formErrorCode.value = e.code;
    }
  } finally {
    if (generation === epoch) busy.value = false;
  }
}
async function save() {
  if (!preview.value || busy.value) return;
  const generation = epoch,
    currentForm = formEpoch;
  busy.value = true;
  formError.value = '';
  formErrorCode.value = '';
  try {
    await request('', 'post', { verification: preview.value.verification });
    if (generation !== epoch) return;
    if (currentForm === formEpoch) dialog.value.close();
    notice.value = 'Группа добавлена. Модерация не включена.';
    await load(1);
  } catch (e) {
    if (generation === epoch && currentForm === formEpoch) {
      formError.value = e.message;
      formErrorCode.value = e.code;
    }
  } finally {
    if (generation === epoch) busy.value = false;
  }
}
watch(
  () => [form.chat_id, form.bot_entry_id],
  () => {
    preview.value = null;
    formError.value = '';
    formErrorCode.value = '';
    formEpoch += 1;
  }
);
function cancel() {
  epoch += 1;
  formEpoch += 1;
  requests.forEach(r => r.abort());
  requests.clear();
}
watch(
  () => route.params.accountId,
  () => {
    cancel();
    dialog.value?.close();
    rows.value = [];
    bots.value = [];
    total.value = 0;
    page.value = 1;
    expandedBots.value = new Set();
    infoEpoch += 1;
    modeDialog.value?.close();
    modeInfo.value = null;
    modeError.value = '';
    modeLoading.value = false;
    receiverReady.value = false;
    modeSaving.value = null;
    modeUncertain.value = new Set();
    receiverPending.value = null;
    receiverPendingMedia.value = null;
    receiverOldestAt.value = null;
    orderSaving.value = false;
    orderUncertain.value = false;
    orderMessage.value = '';
    orderBefore = [];
    orderVersion.value = '';
    busy.value = false;
    notice.value = '';
    reset();
    load(1);
  },
  { immediate: true }
);
onBeforeUnmount(cancel);
</script>

<template>
  <SettingsLayout :is-loading="loading" loading-message="Загружаем группы…">
    <template #header>
      <BaseSettingsHeader
        title="ТГ группы"
        description="Подключённые Telegram-группы. Добавление группы не включает модерацию."
      >
        <template #actions>
          <Button
            label="Обновить список"
            slate
            faded
            :disabled="loading || busy || orderSaving || modeSaving !== null"
            @click="load()"
          />
          <Button
            icon="i-lucide-circle-plus"
            label="Добавить группу"
            :disabled="loading || busy || orderSaving || denied || !bots.length"
            @click="open"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <Banner
        v-if="orderSaving || orderMessage"
        :color="orderUncertain ? 'ruby' : 'teal'"
        role="status"
        class="mb-4"
        >{{ orderSaving ? 'Сохраняем порядок…' : orderMessage }}</Banner
      >
      <Banner v-if="error" color="ruby" role="alert" class="mb-4">{{
        error
      }}</Banner>
      <Banner v-else-if="notice" color="teal" role="status" class="mb-4">{{
        notice
      }}</Banner>
    </template>
    <template #body>
      <template v-if="!denied && !error">
        <p class="mb-2 text-sm text-n-slate-11">
          Количество участников обновляется примерно раз в 6 часов. Изменения
          прав бота поступают из Telegram, когда работает получение сообщений.
          На странице показаны сохранённые данные.
        </p>
        <Banner
          v-if="receiverPending > 0"
          color="blue"
          role="status"
          class="mb-4"
        >
          В обработке {{ count(receiverPending) }} сообщений<span
            v-if="receiverPendingMedia"
            >, из них вложений: {{ count(receiverPendingMedia) }}</span
          >.
          <span v-if="receiverOldestAt"
            >Первое ожидает с {{ date(receiverOldestAt) }}.</span
          >
        </Banner>
        <p id="tg-group-mode-help" class="mb-6 mt-0 text-xs text-n-slate-11">
          Режим задаётся отдельно для каждой группы. Наблюдение только получает
          сообщения — без анализа и действий. Подробности — по значку ⓘ.
        </p>
        <div
          v-if="!rows.length"
          class="flex flex-col items-center justify-center gap-3 py-20 text-center"
        >
          <Icon
            icon="i-lucide-users-round"
            class="size-8 text-n-slate-10"
            aria-hidden="true"
          />
          <p class="text-base text-n-slate-12">Группы ещё не добавлены</p>
          <p class="text-sm text-n-slate-11">
            {{
              bots.length
                ? 'Добавьте бота в группу в Telegram, затем нажмите «Добавить группу».'
                : 'Сначала подключите бота в разделе «ТГ Бот».'
            }}
          </p>
        </div>
        <Draggable
          v-if="rows.length"
          v-model="rows"
          item-key="id"
          v-bind="dragOptions"
          :disabled="loading || busy || orderSaving || orderUncertain"
          class="flex flex-col gap-5 md:hidden"
          @start="startOrder"
          @change="saveOrder"
        >
          <template #item="{ element: group, index }">
            <article class="min-w-0 rounded-xl border border-n-weak p-3">
              <GroupOrderHandle
                :title="group.title"
                :number="(page - 1) * 25 + index + 1"
                :disabled="loading || busy || orderSaving || orderUncertain"
                @move="moveByKey(index, $event)"
              />
              <h2
                class="mb-3 mt-0 break-words text-sm font-medium text-n-slate-12"
              >
                {{ group.title }}
              </h2>
              <SmmPurchaseButton :account-id="String(route.params.accountId)" :group="group" :disabled="loading || busy" />
              <dl
                class="mb-4 mt-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 text-xs text-n-slate-11"
              >
                <dt>Chat ID</dt>
                <dd class="m-0 break-all">{{ group.chat_id }}</dd>
                <dt>@username</dt>
                <dd class="m-0 break-all">
                  {{ group.username ? '@' + group.username : '—' }}
                </dd>
                <dt>Статус</dt>
                <dd
                  class="m-0"
                  :class="
                    group.status === 'connected'
                      ? 'text-n-teal-11'
                      : 'text-n-ruby-11'
                  "
                >
                  {{
                    group.status === 'connected'
                      ? 'Подключена'
                      : 'Ошибка проверки'
                  }}
                </dd>
                <dt class="self-center">Режим</dt>
                <dd class="m-0">
                  <GroupMode
                    :group-title="group.title"
                    :mode="group.mode"
                    :ready="receiverReady"
                    :management-ready="managementReady"
                    :saving="modeSaving === group.id"
                    :disabled="
                      loading ||
                      modeSaving !== null ||
                      modeUncertain.has(group.id)
                    "
                    @change="setMode(group, $event)"
                    @info="showMode(group)"
                  />
                </dd>
                <dt>Участники</dt>
                <dd class="m-0">
                  {{ count(group.member_count)
                  }}<span class="mt-1 block text-n-slate-10">{{
                    date(group.member_count_checked_at)
                  }}</span>
                </dd>
              </dl>
              <p v-if="group.error_code" class="text-xs text-n-ruby-11">
                {{
                  messages[group.error_code] ||
                  'Проверка не выполнена. Показаны последние данные.'
                }}
              </p>
              <Button
                type="button"
                label="Публикации"
                icon="i-lucide-calendar-clock"
                slate
                faded
                class="mb-3"
                @click="openPublications(group)"
              />
              <div class="flex items-center gap-3 text-xs text-n-slate-11">
                <span>Бот</span>
                <Button
                  type="button"
                  :title="
                    expandedBots.has(group.id)
                      ? 'Свернуть информацию о боте'
                      : 'Развернуть информацию о боте'
                  "
                  :icon="
                    expandedBots.has(group.id)
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                  slate
                  faded
                  size="sm"
                  class="min-h-10 min-w-10"
                  :aria-expanded="expandedBots.has(group.id)"
                  :aria-controls="`group-bot-mobile-${group.id}`"
                  :aria-label="`Бот: ${group.title}`"
                  @click="toggleBot(group.id)"
                />
              </div>
              <div
                v-show="expandedBots.has(group.id)"
                :id="`group-bot-mobile-${group.id}`"
                class="mt-3"
              >
                <BotRights
                  v-if="expandedBots.has(group.id)"
                  :snapshot="group.bot_snapshot"
                  :stale="group.status === 'check_failed'"
                />
              </div>
            </article>
          </template>
        </Draggable>
        <div v-if="rows.length" class="hidden overflow-x-auto md:block">
          <Draggable
            v-model="rows"
            tag="table"
            item-key="id"
            v-bind="dragOptions"
            :disabled="loading || busy || orderSaving || orderUncertain"
            class="min-w-full text-sm"
            @start="startOrder"
            @change="saveOrder"
          >
            <template #header>
              <thead>
                <tr class="text-left text-n-slate-11">
                  <th scope="col" class="py-3 pr-4 font-medium">
                    <span class="sr-only">Перемещение и </span>№
                  </th>
                  <th scope="col" class="py-3 pr-4 font-medium">
                    Название группы
                  </th>
                  <th scope="col" class="py-3 pr-4 font-medium">chat_id</th>
                  <th scope="col" class="py-3 pr-4 font-medium">@username</th>
                  <th scope="col" class="py-3 pr-4 font-medium">Статус</th>
                  <th scope="col" class="py-3 pr-4 font-medium">Режим</th>
                  <th scope="col" class="py-3 pr-4 font-medium">Бот</th>
                  <th scope="col" class="py-3 pr-4 font-medium">Публикации</th>
                  <th scope="col" class="py-3 font-medium text-right">
                    Участники
                  </th>
                </tr>
              </thead>
            </template>
            <template #item="{ element: group, index }">
              <tbody class="border-t border-n-weak">
                <tr>
                  <td class="py-4 pr-4">
                    <GroupOrderHandle
                      :title="group.title"
                      :number="(page - 1) * 25 + index + 1"
                      :disabled="
                        loading || busy || orderSaving || orderUncertain
                      "
                      @move="moveByKey(index, $event)"
                    />
                  </td>
                  <td
                    class="py-4 pr-4 min-w-40 max-w-64 break-words font-medium text-n-slate-12"
                  >
                    {{ group.title }}
                    <SmmPurchaseButton :account-id="String(route.params.accountId)" :group="group" :disabled="loading || busy" />
                  </td>
                  <td class="py-4 pr-4 whitespace-nowrap text-n-slate-11">
                    {{ group.chat_id }}
                  </td>
                  <td class="py-4 pr-4 text-n-slate-11">
                    {{ group.username ? '@' + group.username : '—' }}
                  </td>
                  <td class="py-4 pr-4">
                    <span
                      class="inline-flex items-center gap-1.5 rounded-md px-2 py-1 whitespace-nowrap text-xs font-medium"
                      :class="
                        group.status === 'connected'
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-ruby-3 text-n-ruby-11'
                      "
                    >
                      <Icon
                        :icon="
                          group.status === 'connected'
                            ? 'i-lucide-circle-check'
                            : 'i-lucide-circle-alert'
                        "
                        class="size-3.5"
                        aria-hidden="true"
                      />
                      {{
                        group.status === 'connected'
                          ? 'Подключена'
                          : 'Ошибка проверки'
                      }}
                    </span>
                    <p
                      v-if="group.error_code"
                      class="mt-1 mb-0 max-w-56 text-xs text-n-ruby-11"
                    >
                      {{
                        messages[group.error_code] ||
                        'Проверка не выполнена. Показаны последние данные.'
                      }}
                    </p>
                  </td>
                  <td class="py-4 pr-4">
                    <GroupMode
                      :group-title="group.title"
                      :mode="group.mode"
                      :ready="receiverReady"
                      :management-ready="managementReady"
                      :saving="modeSaving === group.id"
                      :disabled="
                        loading ||
                        modeSaving !== null ||
                        modeUncertain.has(group.id)
                      "
                      @change="setMode(group, $event)"
                      @info="showMode(group)"
                    />
                  </td>
                  <td class="py-4 pr-4">
                    <Button
                      type="button"
                      :title="
                        expandedBots.has(group.id)
                          ? 'Свернуть информацию о боте'
                          : 'Развернуть информацию о боте'
                      "
                      :icon="
                        expandedBots.has(group.id)
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      slate
                      faded
                      size="sm"
                      :aria-expanded="expandedBots.has(group.id)"
                      :aria-controls="`group-bot-desktop-${group.id}`"
                      :aria-label="`Бот: ${group.title}`"
                      @click="toggleBot(group.id)"
                    />
                  </td>
                  <td class="py-4 pr-4">
                    <Button
                      type="button"
                      label="Открыть"
                      icon="i-lucide-calendar-clock"
                      slate
                      faded
                      size="sm"
                      :aria-label="`Публикации: ${group.title}`"
                      @click="openPublications(group)"
                    />
                  </td>
                  <td class="py-4 text-right whitespace-nowrap text-n-slate-12">
                    {{ count(group.member_count) }}
                    <p class="mt-1 mb-0 text-xs text-n-slate-11">
                      {{ date(group.member_count_checked_at) }}
                    </p>
                  </td>
                </tr>
                <tr
                  v-show="expandedBots.has(group.id)"
                  :id="`group-bot-desktop-${group.id}`"
                >
                  <td colspan="9" class="pb-5 pt-1">
                    <BotRights
                      v-if="expandedBots.has(group.id)"
                      :snapshot="group.bot_snapshot"
                      :stale="group.status === 'check_failed'"
                    />
                  </td>
                </tr>
              </tbody>
            </template>
          </Draggable>
        </div>
        <div
          v-if="total > 25"
          class="flex items-center justify-end gap-3 py-4 text-sm text-n-slate-11"
        >
          <Button
            label="Назад"
            slate
            faded
            :disabled="page <= 1 || loading || orderSaving"
            @click="load(page - 1)"
          />
          <span>{{ page }} / {{ Math.ceil(total / 25) }}</span>
          <Button
            label="Далее"
            slate
            faded
            :disabled="page * 25 >= total || loading || orderSaving"
            @click="load(page + 1)"
          />
        </div>
      </template>
    </template>
    <Dialog
      ref="dialog"
      :title="preview ? 'Подтвердить добавление' : 'Добавить группу'"
      :description="
        preview
          ? 'Проверьте группу и бота. Модерация останется выключенной.'
          : 'Введите юзернейм без @. Бот должен быть администратором группы с правами удаления сообщений и ограничения участников.'
      "
      width="xl"
      :overflow-y-auto="true"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="reset"
      @confirm="preview ? save() : check()"
    >
      <div
        class="flex min-h-0 max-h-[calc(100dvh-18rem)] flex-col gap-4 overflow-y-auto"
        aria-label="Данные группы"
      >
        <template v-if="!preview">
          <template v-if="bots.length > 1">
            <label
              for="tg-group-bot"
              class="text-sm font-medium text-n-slate-12"
              >Бот</label
            >
            <select
              id="tg-group-bot"
              v-model="form.bot_entry_id"
              :disabled="busy"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
            >
              <option value="" disabled>Выберите бота</option>
              <option v-for="bot in bots" :key="bot.id" :value="bot.id">
                {{ bot.username ? '@' + bot.username : bot.name }}
              </option>
            </select>
          </template>
          <Input
            id="tg-group-chat-id"
            v-model="form.chat_id"
            label="Юзернейм группы"
            placeholder="beauty_nn_52"
            maxlength="33"
            :disabled="busy"
          />
          <p class="m-0 text-sm text-n-slate-11">
            Для закрытой группы — числовой Chat ID. Данные загрузятся
            автоматически через прокси бота.
          </p>
        </template>
        <Banner v-if="busy" color="blue" role="status">{{
          preview ? 'Сохраняем группу…' : 'Проверяем группу…'
        }}</Banner>
        <Banner
          v-if="formError"
          :color="formErrorCode === 'busy' && !preview ? 'blue' : 'ruby'"
          role="alert"
          >{{
            formErrorCode === 'busy' && preview
              ? 'Сохранение пока занято другой операцией. Подождите несколько секунд и снова нажмите «Добавить».'
              : formError
          }}</Banner
        >
        <div v-if="preview" class="flex min-w-0 flex-col gap-4 text-sm">
          <section
            class="rounded-xl border border-n-weak p-4"
            aria-label="Найденная группа"
          >
            <div class="mb-3 flex items-start gap-3">
              <Icon
                icon="i-lucide-users-round"
                class="mt-0.5 size-5 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <div class="min-w-0">
                <h4 class="m-0 break-words text-sm font-medium text-n-slate-12">
                  {{ preview.title }}
                </h4>
                <p
                  class="mb-0 mt-1 flex items-center gap-1 text-xs text-n-teal-11"
                  role="status"
                >
                  <Icon
                    icon="i-lucide-circle-check"
                    class="size-3.5"
                    aria-hidden="true"
                  />Группа найдена
                </p>
              </div>
            </div>
            <dl
              class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs text-n-slate-11"
            >
              <dt>Chat ID</dt>
              <dd class="m-0 break-words">{{ preview.chat_id }}</dd>
              <dt>@username</dt>
              <dd class="m-0 break-words">
                {{ preview.username ? '@' + preview.username : 'Не задан' }}
              </dd>
              <dt>Участники</dt>
              <dd class="m-0">{{ count(preview.member_count) }}</dd>
            </dl>
          </section>
          <BotRights :snapshot="preview.bot_snapshot" compact />
        </div>
      </div>
      <template #footer>
        <div
          class="flex shrink-0 justify-end gap-2 border-t border-n-weak pt-4"
        >
          <Button
            type="button"
            :label="preview ? 'Назад' : 'Отменить'"
            slate
            faded
            :disabled="busy"
            @click="preview ? back() : dialog.close()"
          />
          <Button
            v-if="!preview"
            type="button"
            label="Проверить группу"
            :disabled="!canCheck"
            :is-loading="busy"
            @click="check"
          />
          <Button
            v-else
            type="button"
            label="Добавить"
            :disabled="busy"
            :is-loading="busy"
            @click="save"
          />
        </div>
      </template>
    </Dialog>
    <Dialog
      ref="modeDialog"
      :title="
        modeInfo?.mode === 'manage'
          ? 'Управление'
          : modeInfo?.mode === 'observe'
            ? 'Наблюдение'
            : 'Выключен'
      "
      :description="modeInfo?.title || ''"
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="
        infoEpoch += 1;
        modeLoading = false;
      "
    >
      <div
        class="flex max-h-[calc(100dvh-18rem)] flex-col gap-4 overflow-y-auto text-sm text-n-slate-11"
      >
        <Banner v-if="modeLoading" color="blue" role="status"
          >Проверяем состояние…</Banner
        >
        <Banner v-if="modeError" color="ruby" role="alert"
          >{{ modeError }} Обновите список, чтобы увидеть сохранённый
          режим.</Banner
        >
        <template v-if="modeInfo && !modeLoading && !modeError">
          <p v-if="modeInfo.mode === 'off'" class="m-0">
            Новая система не сохраняет и не анализирует сообщения этой группы,
            не удаляет их и не отвечает. Старая система продолжает работать
            независимо.
          </p>
          <template v-if="modeInfo.mode !== 'off'">
            <p class="m-0">
              Новые сообщения, авторы и вложения появляются в «Диалогах». Бот
              подключается через выбранную прокси.
            </p>
            <p v-if="modeInfo.mode === 'manage'" class="m-0">
              Можно вручную отправлять текст, фото, видео, документы и голосовые
              от имени бота, отвечать на сообщения и удалять их в Telegram. До
              10 вложений, суммарно до 20 МБ. Удаление зависит от прав бота и
              ограничений Telegram.
            </p>
            <p class="m-0">
              Автоответы, анализ спама и блокировки выключены. В «Наблюдении»
              отправка и удаление недоступны. Права бота меняются только в
              Telegram.
            </p>
            <div class="rounded-xl border border-n-weak p-3">
              <p class="mb-3 mt-0 font-medium text-n-slate-12" role="status">
                {{ receiverStatus }}
              </p>
              <dl
                class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-xs"
              >
                <dt>Получено сообщений</dt>
                <dd class="m-0">{{ count(modeInfo.received_count) }}</dd>
                <dt>Последнее получение</dt>
                <dd class="m-0">
                  {{
                    modeInfo.last_received_at
                      ? date(modeInfo.last_received_at)
                      : 'Пока нет'
                  }}
                </dd>
                <dt>Проверка связи</dt>
                <dd class="m-0">
                  {{
                    modeInfo.receiver_checked_at
                      ? date(modeInfo.receiver_checked_at)
                      : 'Ожидается'
                  }}
                </dd>
                <dt>В обработке</dt>
                <dd class="m-0">
                  {{ count(modeInfo.receiver_pending)
                  }}<span v-if="modeInfo.receiver_pending_media">
                    (вложений:
                    {{ count(modeInfo.receiver_pending_media) }})</span
                  >
                </dd>
                <dt v-if="modeInfo.receiver_oldest_pending_at">
                  Первое ожидает с
                </dt>
                <dd v-if="modeInfo.receiver_oldest_pending_at" class="m-0">
                  {{ date(modeInfo.receiver_oldest_pending_at) }}
                </dd>
              </dl>
            </div>
            <p class="m-0 text-xs">
              Получение непрерывное. При сбое связи возможна задержка. Старая
              история не загружается. При неподтверждённой отправке повтор
              автоматически не выполняется — сначала проверьте Telegram.
            </p>
          </template>
          <p class="m-0 text-xs">
            Telegram передаёт события всего бота. События выключенных и
            неизвестных групп отбрасываются без сохранения. Если выключены все
            группы, запросов получения нет.
          </p>
        </template>
      </div>
      <template #footer
        ><div class="flex justify-end gap-2 border-t border-n-weak pt-4">
          <Button
            v-if="modeInfo && !modeError"
            label="Обновить"
            slate
            faded
            :disabled="modeLoading"
            @click="showMode(modeInfo)"
          />
          <Button label="Понятно" @click="modeDialog.close()" /></div
      ></template>
    </Dialog>
  </SettingsLayout>
</template>

<style scoped>
.tg-group-sort-ghost {
  @apply opacity-35 outline outline-2 outline-dashed outline-n-blue-9;
}
.tg-group-sort-chosen {
  @apply bg-n-blue-3;
}
</style>
